import type { SportsbookTranslation } from "../../../../queries/sportsbook-fm/sportsbookTranslations";

import { matchActiveLanguageTag } from "./activeLanguage";

export const SPORTSBOOK_FM_BASE_LOCALE = "en";

export type SportsbookListTranslations = {
  key?: string;
  values?: Record<string, unknown> | null;
};

export type SportsbookListTranslatedEntity = {
  translations?: SportsbookListTranslations | null;
};

export function toTranslationValueEntries(
  entity: SportsbookListTranslatedEntity | null | undefined,
): Array<[string, string]> {
  const values = entity?.translations?.values;
  return values && typeof values === "object" && !Array.isArray(values)
    ? Object.entries(values).filter(
        (entry): entry is [string, string] => typeof entry[1] === "string",
      )
    : [];
}

export function getListEntityBaseName(
  entity: SportsbookListTranslatedEntity | null | undefined,
  fallbackName = "",
): string {
  const base = toTranslationValueEntries(entity).find(
    ([locale]) => locale.toLowerCase() === SPORTSBOOK_FM_BASE_LOCALE,
  );
  return base?.[1]?.trim() ? base[1] : fallbackName;
}

export function toListTranslationEditorData(
  entity: SportsbookListTranslatedEntity | null | undefined,
  fallbackKey: string | undefined,
  fallbackName: string,
): SportsbookTranslation | undefined {
  if (!entity) return undefined;

  return {
    entity_key: entity.translations?.key ?? fallbackKey ?? "",
    base: {
      locale: SPORTSBOOK_FM_BASE_LOCALE,
      value: getListEntityBaseName(entity, fallbackName),
    },
    locales: toTranslationValueEntries(entity).map(([locale, value]) => ({
      locale,
      machine: value,
      override: null,
      status: "translated",
    })),
  };
}

/**
 * Folds saved locale values back into a list row so the table shows them
 * without a refetch. List endpoints return names already localized for the
 * admin, so the display field follows a change in the admin's own language.
 */
export function applyListEntityTranslations<
  T extends SportsbookListTranslatedEntity,
  K extends keyof T,
>(
  entity: T,
  values: Record<string, string>,
  displayField: K,
  key?: string,
): T {
  const entries = Object.entries(values).filter(
    (entry): entry is [string, string] => typeof entry[1] === "string",
  );
  if (entries.length === 0) return entity;

  const mergedKey = entity.translations?.key ?? key;
  const merged = {
    ...entity,
    translations: {
      ...(entity.translations ?? {}),
      ...(mergedKey ? { key: mergedKey } : {}),
      values: {
        ...(entity.translations?.values ?? {}),
        ...Object.fromEntries(entries),
      },
    },
  };

  const activeLocale = matchActiveLanguageTag(entries.map(([locale]) => locale));
  return activeLocale
    ? { ...merged, [displayField]: values[activeLocale] }
    : merged;
}

export function applyListEntityRename<
  T extends SportsbookListTranslatedEntity,
  K extends keyof T,
>(
  entity: T,
  name: string,
  translationsEnabled: boolean,
  displayField: K,
): T {
  if (!translationsEnabled || !entity.translations?.values) {
    return { ...entity, [displayField]: name };
  }

  const current = entity[displayField];
  const currentName = typeof current === "string" ? current : "";

  return {
    ...entity,
    [displayField]:
      currentName === getListEntityBaseName(entity, currentName)
        ? name
        : current,
    translations: {
      ...entity.translations,
      values: {
        ...entity.translations.values,
        [SPORTSBOOK_FM_BASE_LOCALE]: name,
      },
    },
  };
}
